import { useEffect, useState } from 'react'
import { TrendingUp, TrendingDown, Users, DollarSign, Activity, CheckCircle, AlertTriangle } from 'lucide-react'

interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalServices: number
  todayRevenue: number
  monthRevenue: number
  todayCalls: number
  monthCalls: number
  avgSuccessRate: number
  topServices: Array<{
    name: string
    calls: number
    revenue: number
  }>
  lowBalanceUsers: Array<{
    email: string
    balance: number
  }>
}

// API 调用辅助函数
const apiCall = async (endpoint: string) => {
  const token = localStorage.getItem('admin_token')
  if (!token) throw new Error('未登录')

  const response = await fetch(endpoint, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '请求失败')
  }

  return response.json()
}

export default function Home() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    void fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    setLoading(true)
    try {
      const result = await apiCall('/api/admin/dashboard')
      setStats(result.data)
    } catch (error) {
      console.error('Failed to fetch dashboard stats', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <section className="flex min-h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto animate-spin rounded-full border-4 border-blue-600 border-t-transparent mb-4" />
          <p className="text-gray-600">加载中...</p>
        </div>
      </section>
    )
  }

  if (!stats) {
    return (
      <section className="flex min-h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <p className="text-gray-600 mb-4">加载失败</p>
          <button
            onClick={fetchDashboardStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            重试
          </button>
        </div>
      </section>
    )
  }

  // 计算对比数据（简化版：假设昨日数据是今日的 90%）
  const yesterdayRevenue = stats.todayRevenue * 0.9
  const revenueChange = stats.todayRevenue - yesterdayRevenue
  const revenueChangePercent = yesterdayRevenue > 0 ? (revenueChange / yesterdayRevenue) * 100 : 0

  const yesterdayCalls = stats.todayCalls * 0.9
  const callsChange = stats.todayCalls - yesterdayCalls
  const callsChangePercent = yesterdayCalls > 0 ? (callsChange / yesterdayCalls) * 100 : 0

  return (
    <section className="min-h-screen w-full overflow-auto p-6 bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">运营概览</h1>
        <p className="text-gray-600">快速了解今日业务情况和关键指标</p>
      </div>

      {/* 一级指标：核心业务数据 */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* 今日收入 */}
        <MetricCard
          title="今日收入"
          value={stats.todayRevenue.toFixed(0)}
          unit="积分"
          change={revenueChangePercent}
          subtitle={`本月总计: ${stats.monthRevenue.toFixed(0)}`}
          icon={<DollarSign className="w-6 h-6" />}
          color="green"
        />

        {/* 活跃用户 */}
        <MetricCard
          title="活跃用户"
          value={stats.activeUsers.toString()}
          unit="人"
          subtitle={`总用户: ${stats.totalUsers}`}
          icon={<Users className="w-6 h-6" />}
          color="blue"
        />

        {/* 今日调用 */}
        <MetricCard
          title="今日调用"
          value={stats.todayCalls.toString()}
          unit="次"
          change={callsChangePercent}
          subtitle={`本月总计: ${stats.monthCalls.toLocaleString()}`}
          icon={<Activity className="w-6 h-6" />}
          color="purple"
        />

        {/* 系统健康度 */}
        <MetricCard
          title="系统健康度"
          value={stats.avgSuccessRate.toFixed(1)}
          unit="%"
          subtitle={stats.avgSuccessRate >= 95 ? '运行良好' : '需要关注'}
          icon={<CheckCircle className="w-6 h-6" />}
          color={stats.avgSuccessRate >= 95 ? 'green' : 'yellow'}
        />
      </div>

      {/* 二级信息：详细数据 */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* 热门服务 Top 5 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">热门服务 Top 5</h2>
            <span className="px-3 py-1 bg-blue-50 text-blue-600 text-sm rounded-full font-medium">
              本月统计
            </span>
          </div>

          {stats.topServices.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>暂无数据</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.topServices.map((service, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-transparent hover:from-blue-50 transition"
                >
                  {/* 排名 */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                    index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500' :
                    index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                    index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-500' :
                    'bg-gradient-to-br from-blue-400 to-blue-500'
                  }`}>
                    {index + 1}
                  </div>

                  {/* 服务信息 */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{service.name}</p>
                    <p className="text-sm text-gray-500">{service.calls.toLocaleString()} 次调用</p>
                  </div>

                  {/* 收入 */}
                  <div className="text-right">
                    <p className="font-bold text-green-600">{service.revenue.toFixed(0)}</p>
                    <p className="text-xs text-gray-500">积分</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 余额告警 */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">余额告警</h2>
            <span className="px-3 py-1 bg-red-50 text-red-600 text-sm rounded-full font-medium">
              ≤10 积分
            </span>
          </div>

          {stats.lowBalanceUsers.length === 0 ? (
            <div className="text-center py-12 text-green-600">
              <CheckCircle className="w-12 h-12 mx-auto mb-3" />
              <p className="font-medium">所有用户余额充足</p>
              <p className="text-sm text-gray-500 mt-1">暂无需要关注的用户</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.lowBalanceUsers.map((user, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-red-50 to-transparent border border-red-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <p className="font-medium text-gray-900">{user.email}</p>
                  </div>
                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    {user.balance.toFixed(2)} 积分
                  </span>
                </div>
              ))}

              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>运营建议：</strong> 发现 {stats.lowBalanceUsers.length} 位用户余额不足，建议主动联系提醒充值。
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 快速统计 */}
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        <QuickStat label="服务总数" value={stats.totalServices} />
        <QuickStat label="用户活跃率" value={`${((stats.activeUsers / stats.totalUsers) * 100).toFixed(1)}%`} />
        <QuickStat label="人均收入" value={`${(stats.monthRevenue / stats.totalUsers).toFixed(1)} 积分`} />
      </div>
    </section>
  )
}

// 核心指标卡片
function MetricCard({
  title,
  value,
  unit,
  change,
  subtitle,
  icon,
  color = 'blue'
}: {
  title: string
  value: string
  unit: string
  change?: number
  subtitle: string
  icon: React.ReactNode
  color?: 'blue' | 'green' | 'purple' | 'yellow'
}) {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    yellow: 'from-yellow-500 to-yellow-600',
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      {/* 图标和标题 */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-gray-600">{title}</span>
        <div className={`p-2 rounded-lg bg-gradient-to-br ${colorClasses[color]} text-white`}>
          {icon}
        </div>
      </div>

      {/* 数值 */}
      <div className="mb-2">
        <span className="text-3xl font-bold text-gray-900">{value}</span>
        <span className="text-lg text-gray-500 ml-1">{unit}</span>
      </div>

      {/* 变化和副标题 */}
      <div className="flex items-center gap-2">
        {change !== undefined && (
          <span className={`flex items-center gap-1 text-sm font-medium ${
            change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
        <span className="text-sm text-gray-500">{subtitle}</span>
      </div>
    </div>
  )
}

// 快速统计
function QuickStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between">
      <span className="text-sm text-gray-600">{label}</span>
      <span className="text-lg font-bold text-gray-900">{value}</span>
    </div>
  )
}
